/**
 * Karbon360° v2 — Records.
 *
 * Every activity record behind the inventory, with the calculation that produced
 * it. This is the screen the whole Phase 0 provenance work exists to serve: an
 * assurance reviewer asks "where did this number come from?" and the drawer
 * answers with the activity, the factor, the factor version, and whether the
 * stored figure still reconciles.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ErrorBoundary from '../components/ErrorBoundary';
import { Shell } from '../components/Shell';
import { X } from '../components/v2/Icons';
import {
  PageHeader, Card, Button, Badge, Callout, Empty, Skeleton, Tabs,
  SearchInput, Select, Drawer, DList, Sect, CalcTrace, TableWrap, SortHeader,
  DataQuality, useToast, fmt,
} from '../components/v2/ui';
import { useT } from '../i18n';
import { DocPreview } from '../components/v2/DocPreview';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));

/** Unit the FACTOR is expressed per, which is not always the unit entered. */
function factorUnitFor(r) {
  if (r.record_type === 'scope2-process') return 'kWh';
  if (r.record_type === 'scope1-process') return 't';
  if (r.record_type === 'scope3') return 'pkm';
  return r.unit || 'unit';
}

/**
 * Evidence tier, derived from what we can actually evidence today:
 *   3 — a source document is attached (metered/invoiced)
 *   2 — activity × factor, verified by the server
 *   1 — no verification recorded (pre-migration row)
 * NOT a claimed measured/estimated/spend tier — we do not store one.
 */
function tierFor(r) {
  if (r.invoice_id || r.source === 'ocr') return 3;
  if (r.calc_check === 'match') return 2;
  return 1;
}

const SCOPE_TONE = { scope1: 'out', scope2: 'info', scope3: 'warn' };

function Records() {
  const { t } = useT();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [rows, setRows] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const [site, setSite] = useState('');
  const [source, setSource] = useState('');
  const [sort, setSort] = useState({ key: 'created_at', dir: -1 });
  const [open, setOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API_URL}/api/invoices`, auth()).then((r) => r.data),
      axios.get(`${API_URL}/api/buildings`, auth()).then((r) => r.data).catch(() => []),
    ])
      .then(([recs, blds]) => {
        setRows(Array.isArray(recs) ? recs : []);
        setBuildings(Array.isArray(blds) ? blds : blds?.buildings || []);
      })
      .catch((e) => setError(e.response?.data?.message || 'Could not load your records.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const siteName = useMemo(() => {
    const m = new Map(buildings.map((b) => [String(b.id), b.name]));
    return (id) => (id === null || id === undefined ? '— unassigned —' : m.get(String(id)) || `Facility ${id}`);
  }, [buildings]);

  const emissionRows = useMemo(() => rows.filter((r) => r.type === 'emission_record'), [rows]);
  const mismatchCount = useMemo(() => emissionRows.filter((r) => r.calc_check === 'mismatch').length, [emissionRows]);
  const unverifiedCount = useMemo(() => emissionRows.filter((r) => !r.calc_check).length, [emissionRows]);

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let out = emissionRows.filter((r) => {
      if (tab === 'attention' && r.calc_check !== 'mismatch') return false;
      if (tab.startsWith('scope') && r.scope !== tab) return false;
      if (site && String(r.building_id ?? '') !== site) return false;
      if (source && r.source !== source) return false;
      if (t && !`${r.title} ${r.category} ${siteName(r.building_id)} ${r.id}`.toLowerCase().includes(t)) return false;
      return true;
    });
    out = [...out].sort((a, b) => {
      let va; let vb;
      if (sort.key === 'co2') { va = num(a.emissions_tco2e) || 0; vb = num(b.emissions_tco2e) || 0; }
      else if (sort.key === 'site') { va = siteName(a.building_id); vb = siteName(b.building_id); }
      else if (sort.key === 'period') { va = a.period || ''; vb = b.period || ''; }
      else { va = a.created_at || ''; vb = b.created_at || ''; }
      return va > vb ? sort.dir : va < vb ? -sort.dir : 0;
    });
    return out;
  }, [emissionRows, tab, q, site, source, sort, siteName]);

  const removeRecord = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${r.title}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/api/invoices/${r.id}`, auth());
      toast('Record deleted');
      setOpen(null);
      load();
    } catch (e) {
      toast(e.response?.data?.message || 'Could not delete the record', { tone: 'err' });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="k2-wrap">
        <PageHeader title={t('rec.title')} desc={t('rec.loading')} />
        <Card><div style={{ display: 'grid', gap: 10 }}>
          {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={26} />)}
        </div></Card>
      </div>
    );
  }
  if (error) {
    return <div className="k2-wrap"><PageHeader title={t('rec.title')} /><Callout tone="err">{error}</Callout></div>;
  }

  return (
    <div className="k2-wrap">
      <PageHeader
        title={t('rec.title')}
        desc="Every activity record behind the inventory — its source, the emission factor applied, and whether the stored figure still reconciles."
      >
        <Button icon={X.Plus} variant="mint" onClick={() => navigate('/regjistrimi')}>Add data</Button>
      </PageHeader>

      {mismatchCount > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Callout tone="warn">
            <b>{mismatchCount} record{mismatchCount > 1 ? 's do' : ' does'} not reconcile with the factor on file.</b>{' '}
            The stored figure differs from what the factor and activity produce. Open a record to see the trace.
            <Button size="sm" style={{ marginLeft: 10 }} onClick={() => setTab('attention')}>Review</Button>
          </Callout>
        </div>
      )}

      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { id: 'all', label: 'All', count: emissionRows.length },
          { id: 'attention', label: 'Needs attention', count: mismatchCount },
          { id: 'scope1', label: 'Scope 1' },
          { id: 'scope2', label: 'Scope 2' },
          { id: 'scope3', label: 'Scope 3' },
        ]}
      />

      <div className="k2-fb">
        <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('rec.search')} />
        <Select value={site} onChange={(e) => setSite(e.target.value)} style={{ width: 'auto', minWidth: 190 }}>
          <option value="">All facilities</option>
          {buildings.map((b) => <option key={b.id} value={String(b.id)}>{b.name}</option>)}
        </Select>
        <Select value={source} onChange={(e) => setSource(e.target.value)} style={{ width: 'auto', minWidth: 150 }}>
          <option value="">Any source</option>
          <option value="ocr">Scanned (OCR)</option>
          <option value="manual">Manual</option>
        </Select>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--n500)' }} className="k2-num">
          {filtered.length} of {emissionRows.length} records
        </span>
      </div>

      <TableWrap
        toolbar={
          <>
            <span style={{ fontSize: 12.5, color: 'var(--n500)' }}>
              {unverifiedCount > 0
                ? `${unverifiedCount} record${unverifiedCount > 1 ? 's' : ''} predate verification`
                : 'Every record carries a calculation trace'}
            </span>
            <Button size="sm" variant="gh" style={{ marginLeft: 'auto' }} icon={X.Doc} onClick={load}>Refresh</Button>
          </>
        }
        footer={<span className="k2-num">Showing {filtered.length} of {emissionRows.length}</span>}
      >
        <table className="k2-t">
          <thead>
            <tr>
              <th>Record</th>
              <SortHeader id="site" label={t('rec.facility')} sort={sort} setSort={setSort} />
              <th>Scope</th>
              <th>Evidence</th>
              <th>Source</th>
              <SortHeader id="period" label={t('rec.period')} sort={sort} setSort={setSort} />
              <SortHeader id="co2" label="tCO₂e" sort={sort} setSort={setSort} right />
              <th>Check</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const bad = r.calc_check === 'mismatch';
              return (
                <tr key={r.id} onClick={() => setOpen(r)} style={{ cursor: 'pointer' }}>
                  <td>
                    <div className="k2-cellflex">
                      <span className={`k2-tsq${bad ? '' : ' k2-mint'}`}
                            style={bad ? { background: 'var(--warn-bg)', color: 'var(--warn)' } : undefined}>
                        {bad ? <X.Warn s={14} /> : <X.Doc s={14} />}
                      </span>
                      <span style={{ minWidth: 0 }}>
                        <span className="k2-nm">{r.title}</span>
                        <span className="k2-sub2 k2-mono">{r.id}</span>
                      </span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--n600)' }}>{siteName(r.building_id)}</td>
                  <td><Badge tone={SCOPE_TONE[r.scope]}>{String(r.scope || '').replace('scope', 'Scope ')}</Badge></td>
                  <td><DataQuality q={tierFor(r)} showLabel={false} /></td>
                  <td><Badge tone={r.source === 'ocr' ? 'mint' : undefined}>{r.source === 'ocr' ? 'Scanned' : 'Manual'}</Badge></td>
                  <td className="k2-num" style={{ color: 'var(--n600)' }}>{r.period}</td>
                  <td className="k2-r k2-num" style={{ fontWeight: 600 }}>{fmt(num(r.emissions_tco2e) ?? 0, 3)}</td>
                  <td>
                    {r.calc_check === 'match' && <Badge tone="ok">Verified</Badge>}
                    {r.calc_check === 'mismatch' && <Badge tone="err" dot>Mismatch</Badge>}
                    {!r.calc_check && <Badge tone="out">Not verified</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <Empty
            icon={X.Search}
            title={t('rec.noMatch')}
            body={t('rec.noMatchBody')}
            action={<Button size="sm" onClick={() => { setQ(''); setSite(''); setSource(''); setTab('all'); }}>Clear all filters</Button>}
          />
        )}
      </TableWrap>

      {open && (
        <RecordDrawer
          r={open}
          siteName={siteName}
          onClose={() => setOpen(null)}
          onDelete={removeRecord}
          deleting={deleting}
        />
      )}
    </div>
  );
}

/* ── the audit view ──────────────────────────────────────────────────── */
function RecordDrawer({ r, siteName, onClose, onDelete, deleting }) {
  const { t } = useT();
  // Source-document preview: records only carry invoice_id, so the file path
  // is fetched lazily when the drawer opens for a record with evidence.
  const [doc, setDoc] = useState({ state: r.invoice_id ? 'loading' : 'none', filePath: null, fileName: null });
  useEffect(() => {
    let aborted = false;
    if (!r.invoice_id) { setDoc({ state: 'none', filePath: null, fileName: null }); return undefined; }
    setDoc({ state: 'loading', filePath: null, fileName: null });
    axios.get(`${API_URL}/api/invoices/${r.id}/details`, auth())
      .then((res) => {
        if (aborted) return;
        const fp = res.data?.file_path || null;
        setDoc({ state: fp ? 'ready' : 'none', filePath: fp, fileName: res.data?.file_name || null });
      })
      .catch(() => { if (!aborted) setDoc({ state: 'none', filePath: null, fileName: null }); });
    return () => { aborted = true; };
  }, [r.id, r.invoice_id]);
  const stored = num(r.emissions_tco2e) ?? 0;
  const factor = num(r.factor_value);
  const server = num(r.server_tco2e);
  const fUnit = factorUnitFor(r);

  // The activity the server actually used, in the factor's own unit. Derived
  // from the server's own result rather than the raw entered quantity, because
  // the calculator converts units (kg -> tonnes, m3 -> l) before applying the
  // factor. The comparison below is still against the STORED figure, so this
  // cannot become self-confirming.
  const activity = factor && server !== null && factor !== 0 ? (server * 1000) / factor : null;

  return (
    <Drawer
      title={r.title}
      sub={<span className="k2-mono">{r.id}</span>}
      badge={
        r.calc_check === 'match' ? <Badge tone="ok" lg>Verified</Badge>
          : r.calc_check === 'mismatch' ? <Badge tone="err" lg dot>Does not reconcile</Badge>
            : <Badge tone="out" lg>Not verified</Badge>
      }
      onClose={onClose}
      footer={
        <>
          <Button variant="dgr" icon={X.Close} onClick={() => onDelete(r)} disabled={deleting} style={{ marginRight: 'auto' }}>
            {deleting ? 'Deleting…' : 'Delete record'}
          </Button>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {r.calc_check === 'mismatch' && (
        <div style={{ marginBottom: 18 }}>
          <Callout tone="warn">
            <b>The stored figure does not match the factor on file.</b> It was most likely booked under a
            different factor than the one now in the library. Nothing has been restated — this is flagged for review.
          </Callout>
        </div>
      )}
      {!r.calc_check && (
        <div style={{ marginBottom: 18 }}>
          <Callout tone="info">
            This record predates server-side verification, so no calculation trace was captured at the time.
          </Callout>
        </div>
      )}

      <Sect title={t('rec.result')}>
        <div className="k2-g2">
          <div className="k2-st">
            <div className="k2-lb2">Emissions (as filed)</div>
            <div className="k2-v">{fmt(stored, 3)}<span className="k2-u">tCO₂e</span></div>
          </div>
          <div className="k2-st">
            <div className="k2-lb2">Activity as entered</div>
            <div className="k2-v">{fmt(num(r.quantity) ?? 0, 2)}<span className="k2-u">{r.unit}</span></div>
          </div>
        </div>
      </Sect>

      {factor !== null && activity !== null ? (
        <Sect title={t('rec.trace')}>
          <CalcTrace
            activity={activity}
            activityUnit={fUnit}
            factor={factor}
            factorUnit={`kgCO₂e/${fUnit}`}
            result={stored}
          />
          <div style={{ marginTop: 12 }}>
            <DList rows={[
              ['Emission factor', <span key="f">{r.factor_key || '—'}<br />
                <span style={{ fontSize: 11.5, color: 'var(--n500)' }}>{factor} kgCO₂e/{fUnit}</span></span>],
              ['Factor version', r.factor_version || '—'],
              ['Server recomputed', server === null ? '—' : `${fmt(server, 6)} tCO₂e`],
              ['GHG scope', String(r.scope || '').replace('scope', 'Scope ')],
              ['Evidence tier', <DataQuality key="dq" q={tierFor(r)} />],
            ]} />
          </div>
        </Sect>
      ) : (
        <Sect title={t('rec.trace')}>
          <Callout tone="info">
            No factor was recorded against this row, so the calculation cannot be reproduced here.
          </Callout>
        </Sect>
      )}

      {doc.state !== 'none' && (
        <Sect title={t('rec.sourceDoc')}>
          {doc.state === 'loading' ? (
            <Skeleton h={180} />
          ) : (
            <>
              <DocPreview filePath={doc.filePath} height={340} />
              {doc.fileName && (
                <div style={{ marginTop: 8, fontSize: 12, color: 'var(--n500)' }} className="k2-mono">{doc.fileName}</div>
              )}
            </>
          )}
        </Sect>
      )}

      <Sect title={t('rec.record')}>
        <DList rows={[
          ['Facility', siteName(r.building_id)],
          ['Reporting period', r.period],
          ['Category', r.category],
          r.fuel_key ? ['Fuel', r.fuel_key] : null,
          r.process_type ? ['Process', r.process_type] : null,
          r.vehicle_type ? ['Vehicle', r.vehicle_type] : null,
          r.activity_type ? ['Activity type', r.activity_type] : null,
          r.distance_km ? ['Distance', `${fmt(num(r.distance_km), 0)} km`] : null,
          ['Capture method', <Badge key="s" tone={r.source === 'ocr' ? 'mint' : undefined}>{r.source === 'ocr' ? 'Scanned (OCR)' : 'Manual'}</Badge>],
          ['Source document', r.invoice_id ? `Attached (#${r.invoice_id})` : 'None attached'],
          ['Recorded', r.created_at ? new Date(r.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'],
        ]} />
      </Sect>
    </Drawer>
  );
}

export default function RecordsPage() {
  return (
    <ErrorBoundary>
      <Shell>
        <Records />
      </Shell>
    </ErrorBoundary>
  );
}
